import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import saveIcon from '@iconify/icons-eva/save-outline';

// material
import { Stack, TextField, Button, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import MenuPopover from '../MenuPopover';
import { createWithURLSearchParams } from '../../fetch';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectDevicesArray, selectSession } from '../../createSelectorUtils';

// ----------------------------------------------------------------------

const originFile = [
  { label: 'SDVFR', id: 'sdvfr' },
  { label: 'Airmate', id: 'airmate' },
  { label: 'Air Navigation', id: 'air/navigation' }
];

// ----------------------------------------------------------------------

export default function FlightListImportFlight() {
  const ref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();

  const session = useSelector(selectSession);

  const devicesData = useSelector(selectDevicesArray);
  const devices = useMemo(
    () =>
      session.administrator ? devicesData : devicesData.filter((device) => device.groupId === 0),
    [devicesData, session.administrator]
  );

  const [selectedId, setSelectedId] = useState(null);

  const [application, setApplication] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleChangeFile(event) {
    const reader = new FileReader();
    reader.readAsText(event.target.files[0]);
    reader.onload = (e) => {
      setData(e.target.result);
    };
  }
  const handleClickImport = async () => {
    setIsSubmitting(true);
    const response = await createWithURLSearchParams(`import/flight/${application}`, {
      data,
      deviceId: selectedId
    });
    if (response.ok) {
      enqueueSnackbar(t('FL_Import_Snackbar'), { variant: 'success' });
      setIsOpen(false);
    } else {
      const error = await response.text();
      const message = error.slice(0, error.indexOf('-'));
      enqueueSnackbar(message, { variant: 'error' });
    }
    setIsSubmitting(false);
  };
  return (
    <>
      <Button
        disableRipple
        color="inherit"
        ref={ref}
        onClick={() => setIsOpen(true)}
        endIcon={<Icon icon="bx:import" />}
      >
        {t('Fl_Import')}
      </Button>
      <MenuPopover
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'auto' }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack sx={{ p: 3 }} spacing={3}>
          <Autocomplete
            fullWidth
            options={originFile}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label={t('Fl_Import_Application')} />}
            onChange={(event, value) => setApplication(value.id)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Autocomplete
            fullWidth
            options={devices}
            getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
            renderInput={(params) => <TextField {...params} label={t('devicesList')} />}
            onChange={(event, value) => setSelectedId(value.id)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Stack spacing={3}>
            <input
              accept={
                application === 'airmate' || application === 'air/navigation' ? '.gpx' : '.kml'
              }
              id="icon-button-file"
              type="file"
              hidden
              onChange={handleChangeFile}
            />
            <label htmlFor="icon-button-file">
              <Button
                fullWidth
                size="large"
                type="submit"
                color="inherit"
                variant="outlined"
                startIcon={<Icon icon="fa-solid:file-upload" />}
                component="span"
                disabled={application === null}
              >
                {t('btn_Upload_file')}
              </Button>
            </label>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              onClick={() => handleClickImport()}
              startIcon={<Icon icon={saveIcon} />}
              disabled={data === null || selectedId === null || application === null}
            >
              {t('btn_Save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </MenuPopover>
    </>
  );
}
